import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Compass, BookMarked, Loader2, Check, X } from "lucide-react";
import { t, useLang } from "../lib/i18n";

export default function ListsPage() {
  useLang();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [renamingId, setRenamingId] = useState("");
  const [renameDraft, setRenameDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sections");
      setLists(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await api.post("/sections", { name: n });
      setName("");
      toast.success(`${t("lists.created_toast")} "${n}"`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startRename = (list) => { setRenamingId(list.id); setRenameDraft(list.name); };
  const cancelRename = () => { setRenamingId(""); setRenameDraft(""); };
  const saveRename = async () => {
    const n = renameDraft.trim();
    if (!n) return;
    try {
      await api.patch(`/sections/${renamingId}`, { name: n });
      toast.success(t("lists.renamed_toast"));
      cancelRename();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeList = async (list) => {
    if (!window.confirm(`${t("lists.confirm_delete")} — "${list.name}"`)) return;
    try {
      await api.delete(`/sections/${list.id}`);
      toast.success(t("lists.deleted_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="lists-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">{t("lists.eyebrow")}</div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight">{t("lists.title")}</h1>

      {/* Explainer */}
      <section className="mt-8 border border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] p-6 md:p-8" data-testid="lists-explainer">
        <div className="grid md:grid-cols-[1fr_2fr] gap-8 md:gap-10">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#8C7F65] mb-2">{t("lists.what_is")}</div>
            <h2 className="font-heading text-2xl md:text-3xl text-[#0A0A0A] tracking-tight leading-snug">{t("lists.group_theme")}</h2>
          </div>
          <div className="space-y-3 text-[15px] text-[#0A0A0A] leading-relaxed">
            <p>{t("lists.explain1")}</p>
            <p>{t("lists.explain2")}</p>
          </div>
        </div>
      </section>

      <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight mt-14 mb-4">{t("lists.make_a_list")}</h2>
      <form onSubmit={create} className="flex gap-3 mb-12 max-w-md" data-testid="list-create-form">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("lists.example_placeholder")}
          maxLength={60}
          className="flex-1 bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
          data-testid="list-name"
          required
        />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm disabled:opacity-50 transition-colors"
          data-testid="list-create"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("lists.create_btn")}
        </button>
      </form>

      {loading ? (
        <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">{t("common.loading")}</div>
      ) : lists.length === 0 ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center">
          <div className="font-heading italic text-2xl text-[#525252]">{t("lists.no_lists")}</div>
          <p className="text-sm text-[#525252] mt-3">{t("lists.make_one_hint")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]" data-testid="lists-grid">
          {lists.map((list) => {
            const count = (list.book_ids || []).length;
            const isRenaming = renamingId === list.id;
            return (
              <div key={list.id} className="bg-[#FAF5E6] p-6 flex flex-col" data-testid={`list-${list.id}`}>
                {isRenaming ? (
                  <div className="flex gap-2 mb-4">
                    <input
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") cancelRename(); }}
                      autoFocus maxLength={60}
                      className="flex-1 bg-white border border-[rgba(10,10,10,0.12)] rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                      data-testid={`list-rename-input-${list.id}`}
                    />
                    <button onClick={saveRename} className="p-1.5 hover:bg-black/5 rounded-sm" aria-label="Save" data-testid={`list-rename-save-${list.id}`}>
                      <Check className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button onClick={cancelRename} className="p-1.5 hover:bg-black/5 rounded-sm" aria-label="Cancel">
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-heading text-xl text-[#0A0A0A] tracking-tight" data-testid={`list-name-${list.id}`}>{list.name}</h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => startRename(list)} className="p-1.5 hover:bg-black/5 rounded-sm" aria-label="Rename" data-testid={`list-rename-${list.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-[#525252]" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => removeList(list)} className="p-1.5 hover:bg-[#7C1D1D]/10 rounded-sm" aria-label="Delete" data-testid={`list-delete-${list.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-[#7C1D1D]" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-5">
                  {count} {count === 1 ? t("lists.book_singular") : t("lists.books_plural")}
                </div>
                <div className="mt-auto flex gap-3 pt-3 border-t border-[rgba(10,10,10,0.08)]">
                  <Link to={`/lists/${list.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors" data-testid={`list-open-${list.id}`}>
                    <BookMarked className="w-3 h-3" strokeWidth={1.5} /> {t("lists.open")}
                  </Link>
                  <button
                    onClick={() => navigate(`/recommendations?section=${list.id}`)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors"
                    data-testid={`list-picks-${list.id}`}
                  >
                    <Compass className="w-3 h-3" strokeWidth={1.5} /> {t("lists.get_picks")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
